<template>
  <b-modal id="customer-show-invoice" class="customer-show-invoice" ref="modal" :title="dialog_title" hide-footer>

    <div class="row" v-if="is_error">
      <div class="col">
        <div class="alert alert-danger">
          No se pudo publicar la factura, contacta con <em>developer@wearewabi.com</em> para más información. Puedes
          editar la factura y volver a publicarla.
        </div>
      </div>
    </div>

    <div class="row" v-if="has_notice">
      <div class="col">
        <div class="alert alert-warning">
          La factura se publicó con errores.
          Anula o rectifica la factura para poder validar la operación.
          Consulta con <em>developer@wearewabi.com</em> para más información.
          <hr />
          Detalles del error: {{ notice_tooltip }}
        </div>
      </div>
    </div>

    <div class="row" v-if="is_nif_empty">
      <div class="col">
        <div class="alert alert-warning">
          La factura no tiene NIF asociado. Por favor rellena los datos marcando la casilla "Facturar otro nombre" o
          rellena el NIF en la ficha del paciente.
          Si se trata de menor de edad o incapacitado legal, por favor rellena el NIF del representante.
        </div>
      </div>
    </div>

    <div class="row form-group">
      <div class="col-sm-6">
        <input type="date" class="form-control wabident-xsmall" v-model="data.date"
          :readonly="!(is_admin && is_status_editable)" />
        <label class="wabident-info">FECHA</label>
      </div>
      <div class="col-sm-6">
        <input type="text" class="form-control wabident-xsmall" v-model="invoice_code" readonly />
        <label class="wabident-info">CÓDIGO</label>
      </div>
    </div>

    <div class="row form-group">

      <div class="col-sm-2">
        <div class="form-check form-check-inline">
          <input v-model="is_other_customer" class="form-check-input" type="checkbox" id="is_other_customer"
            value="true" :disabled="!is_status_editable" />
          <label class="form-check-label" for="is_other_customer">A otro nombre</label>
        </div>
      </div>

      <div class="col-sm-2">
        <div class="form-check form-check-inline">
          <input v-model="is_revocation" class="form-check-input" type="checkbox" id="is_revocation" value="true"
            :disabled="!is_status_editable || is_editing" />
          <label class="form-check-label" for="is_revocation">Rectificativa</label>
        </div>
      </div>

      <div class="col-sm-4" v-if="is_revocation">
        <select v-model="revocation_type" id="revocation_type" class="custom-select wabident-xsmall"
          :disabled="!is_status_editable">
          <option value="difference">DIFERENCIA</option>
          <option value="substitution">SUSTITUCIÓN</option>
        </select>
        <label class="wabident-info" for="revocation_type">Tipo Rectificación</label>
      </div>

      <div v-if="is_revocation" class="col-sm-4">
        <v-select class="wabident-selector" id="revocation_invoice_id" :filterable="false" :options="unrevoked_options"
          :reduce="(option) => option.value" @search="searchUnrevoked" placeholder="Busca la factura ... "
          v-model="revocation_invoice_id"></v-select>
        <label for="revocation_invoice_id" class="wabident-info">
          Factura a rectificar
        </label>
      </div>
    </div>

    <div v-if="!is_other_customer" class="row form-group">
      <div class="col" v-if="customer">
        <div class="customer-info">
          <div class="customer-photo" :style="{ 'background-image': 'url(' + customer.photo + ')' }"></div>
          <div class="customer-data">
            {{ customer_real_name }}
            <br />
            {{ customer.phone || customer.phone_alt }} | {{ customer_real_nif }}
          </div>
        </div>
      </div>
    </div>

    <div v-else class="row form-group">
      <div class="col-sm-6">
        <input type="text" class="form-control wabident-xsmall" v-model="customer_name"
          :readonly="!is_status_editable" />
        <label class="wabident-info">Nombre completo</label>
      </div>
      <div class="col-sm-6">
        <input type="text" class="form-control wabident-xsmall" v-model="customer_nif"
          :readonly="!is_status_editable" />
        <label class="wabident-info">DNI/NIF</label>
      </div>
      <div class="col-sm-6">
        <input type="tel" class="form-control wabident-xsmall" v-model="customer_phone"
          :readonly="!is_status_editable" />
        <label class="wabident-info">Teléfono</label>
      </div>
      <div class="col-sm-6">
        <input type="email" class="form-control wabident-xsmall" v-model="customer_email"
          :readonly="!is_status_editable" />
        <label class="wabident-info">Email</label>
      </div>
      <div class="col-sm-12">
        <input type="text" class="form-control wabident-xsmall" v-model="customer_address"
          :readonly="!is_status_editable" />
        <label class="wabident-info">Dirección</label>
      </div>
      <div class="col-sm-4">
        <input type="text" class="form-control wabident-xsmall" v-model="customer_zipcode"
          :readonly="!is_status_editable" />
        <label class="wabident-info">CP</label>
      </div>
      <div class="col-sm-4">
        <input type="text" class="form-control wabident-xsmall" v-model="customer_city"
          :readonly="!is_status_editable" />
        <label class="wabident-info">Ciudad</label>
      </div>
      <div class="col-sm-4">
        <input type="text" class="form-control wabident-xsmall" v-model="customer_province"
          :readonly="!is_status_editable" />
        <label class="wabident-info">Provincia</label>
      </div>
    </div>

    <div class="row form-group">
      <div class="col">
        <table class="table lines-table">
          <colgroup>
            <col class="line-desc-col" style="width: 40%" />
            <col class="line-price-col" />
            <col class="line-vat-col" />
            <col class="line-discount-col" />
            <col class="line-discount-col" />
            <col class="line-total-col" />
            <col class="line-actions-col" />
          </colgroup>
          <thead>
            <tr>
              <th>CONCEPTO</th>
              <th>PRECIO (IMP INC)</th>
              <th>IMP%</th>
              <th>CANTIDAD</th>
              <th>PRECIO(€)</th>
              <th>DESC.(%)</th>
              <th v-if="!is_dental">DESC.(€)</th>
              <th>TOTAL</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, index) in data.lines" :key="index">
              <td class="input">
                <input type="text" class="form-control wabident-xsmall" v-model="line.desc"
                  placeholder="Escribe aquí el concepto" :readonly="!is_status_editable" />
              </td>
              <td class="input">
                <input type="number" class="form-control wabident-xsmall" step="0.01" placeholder="0.00"
                  v-model.number="line.price" :readonly="!is_status_editable" />
              </td>
              <td class="input">
                <input type="number" class="form-control wabident-xsmall" step="0.01" placeholder="0.00"
                  v-model.number="line.vat" :readonly="!is_status_editable" />
              </td>
              <td class="input">
                <input type="number" class="form-control wabident-xsmall" min="1" step="1" placeholder="1"
                  v-model.number="line.quantity" :readonly="!is_status_editable" />
              </td>
              <td>{{ getLinePrice(line) }}</td>
              <td class="input">
                <input type="number" class="form-control wabident-xsmall" step="0.01" placeholder="0.00"
                  v-model.number="line.discount" :readonly="!is_status_editable" />
              </td>
              <td v-if="!is_dental" class="input">
                <input type="number" class="form-control wabident-xsmall" step="0.01" placeholder="0.00"
                  v-model.number="line.abs_discount" :readonly="!is_status_editable" />
              </td>
              <td>{{ getLineNetPrice(line) }}</td>
              <td v-if="is_status_editable" @click="removeLine(line, index)" class="trash-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <path fill-rule="evenodd" d="M2 6a1 1 0 0 1 1-1h18a1 1 0 1 1 0 2H3a1 1 0 0 1-1-1z"
                    clip-rule="evenodd" />
                  <path fill-rule="evenodd"
                    d="M10 3a1 1 0 0 0-1 1v1h6V4a1 1 0 0 0-1-1h-4zm7 2V4a3 3 0 0 0-3-3h-4a3 3 0 0 0-3 3v1H5a1 1 0 0 0-1 1v14a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V6a1 1 0 0 0-1-1h-2zM6 7v13a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7H6z"
                    clip-rule="evenodd" />
                  <path fill-rule="evenodd"
                    d="M10 10a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1zM14 10a1 1 0 0 1 1 1v6a1 1 0 1 1-2 0v-6a1 1 0 0 1 1-1z"
                    clip-rule="evenodd" />
                </svg>
              </td>
              <td v-else></td>
            </tr>

            <tr>
              <td>
                <select v-model="selected_line" v-if="is_status_editable" class="custom-select wabident-xsmall">
                  <option :value="null">Añadir linea ...</option>
                  <option v-for="(line, i) in available_lines" :key="i" :value="line">
                    {{ getLineDesc(line) }}
                  </option>
                </select>
              </td>
              <td colspan="4">
                <span v-if="is_status_editable" class="add-line" @click="addLine">
                  <i class="fas fa-plus"></i>
                  Añadir concepto
                </span>
              </td>
              <td>
                {{ pretty_total }}
                <label class="total">NETO</label>
              </td>
              <td></td>
              <td>
                {{ pretty_net_total }}
                <label class="total">TOTAL</label>
              </td>
              <td></td>
            </tr>

            <tr>
              <td colspan="9" class="input">
                <input type="text" class="form-control wabident-small" v-model.number="data.notes" :placeholder="is_status_editable ? 'Escribe aquí notas adicionales' : ''
                  " :readonly="!is_status_editable" />
                <label class="wabident-info">NOTAS</label>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="row form-group">
      <div class="col buttons-column text-center">

        <button @click="onDelete" :disabled="is_loading" v-if="can_delete" type="button"
          class="btn btn-primary btn-wabident bg-danger">
          Borrar&nbsp;<i class="fa fa-trash"></i>
        </button>

        <button @click="onAnull" :disabled="is_loading" v-if="can_anull" type="button"
          class="btn btn-primary btn-wabident">
          Anular&nbsp;<i class="fa fa-ban"></i>
        </button>

        <button @click="onRemedy" :disabled="is_loading" v-if="can_remedy" type="button"
          class="btn btn-primary btn-wabident">
          Subsanar&nbsp;<i class="fa fa-ban"></i>
        </button>

        <button @click="onRevoke" :disabled="is_loading" v-if="can_revoke" type="button"
          class="btn btn-primary btn-wabident">
          Rectificar&nbsp;<i class="fas fa-undo"></i>
        </button>

        <button v-if="can_publish" @click="publish" :disabled="is_loading" type="button"
          class="btn btn-primary btn-wabident green">
          Publicar&nbsp;
          <i v-if="is_loading" class="fa fa-spin fa-circle-notch"></i>
          <i v-else class="fas fa-check-circle"></i>
        </button>

        <button @click="submit" v-if="can_submit" :disabled="is_loading" type="button"
          class="btn btn-primary btn-wabident">
          <span v-if="is_editing">Modificar</span>
          <span v-else>Crear</span>
          <i v-if="is_loading" class="fa fa-spin fa-circle-notch"></i>
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  props: {
    customer: { required: true },
    clinic: { default: null },
  },
  data() {
    return {
      data: {
        date: new Date(),
        code: "",
        lines: [],
        notes: "",
      },
      uninvoiced_lines: [],
      selected_line: null,
      invoice: null,
      is_editing: false,
      is_loading: false,
      is_other_customer: false,
      customer_phone: "",
      customer_nif: "",
      customer_email: "",
      customer_address: "",
      customer_city: "",
      customer_zipcode: "",
      customer_province: "",
      is_revocation: false,
      revocation_type: 'difference',
      revocation_invoice_id: null,
      unrevoked_invoices: null,
      payment_id: null,
    };
  },
  watch: {
    selected_line(val) {
      if (val) {
        this.addUninvoicedLine(val);
        this.selected_line = null;
      }
    },
  },
  computed: {
    invoice_code() {
      if (!this.data.code || this.data.code == "") {
        return "BORRADOR";
      }

      return this.data.code;
    },
    is_admin() {
      if (!this.clinic) return false;
      return this.user_capabilities.admin || false;
    },
    is_minor() {
      if (!this.customer) return false;
      if (this.customer.is_incapacitated) return true;
      let birthday = moment(this.customer.birth_date);
      let date = moment(this.data.date);
      let years = date.diff(birthday, "years");

      return years < 18;
    },
    customer_real_name() {
      if (!this.customer) return "";

      if (this.is_minor && this.customer.adult_name) {
        return "Rep: " + this.customer.adult_name;
      }
      return this.customer.name;
    },
    customer_real_nif() {
      if (!this.customer) return "";

      if (this.is_minor && this.customer.adult_nif) {
        return this.customer.adult_nif;
      }
      return this.customer.nif;
    },
    pretty_total() {
      let total = this.data.lines.reduce((cur, val) => {
        return cur + Number(val.price) * Number(val.quantity);
      }, 0);
      return WabiDent.fmtMoney(total);
    },
    pretty_net_total() {
      let total = this.data.lines.reduce((cur, val) => {
        return (
          cur +
          Number(val.price) *
          Number(val.quantity) *
          (1 - Number(val.discount) / 100) -
          val.abs_discount
        );
      }, 0);
      return WabiDent.fmtMoney(total);
    },
    pretty_invoice_code() {
      return `Factura #${this.data.code}`;
    },
    drafts_enabled() {
      return this.clinic && this.clinic.config.invoices_enable_drafts;
    },
    can_submit() {
      if (this.drafts_enabled) {
        return this.is_status_editable;
      }
      return true;
    },
    can_publish() {
      if (this.drafts_enabled) {
        return this.is_editing &&
          !this.is_nif_empty &&
          (this.invoice.status == "draft" || this.invoice.status == 'error');
      }
      return false;
    },
    can_revoke() {
      if (!this.invoice) {
        return false;
      }

      if (this.invoice.is_revocation) {
        return false;
      }

      if (this.drafts_enabled) {
        return (
          this.is_editing &&
          (this.invoice.status == "published" ||
            this.invoice.status == "legacy")
        );
      }
      return this.is_editing;
    },
    can_anull() {
      if (!this.drafts_enabled) {
        return false;
      }

      return this.is_editing && this.invoice.status == 'published';
    },
    can_remedy() {
      return this.verifactu_enabled && this.has_notice;
    },
    verifactu_enabled() {
      return this.drafts_enabled && this.clinic.config.verifactu_enabled;
    },
    is_status_editable() {
      if (!this.is_editing) {
        return true;
      }

      if (!this.invoice) {
        return false;
      }

      if (this.drafts_enabled) {
        const allowed = {
          draft: true,
          legacy: true,
          error: true,
        };

        return allowed[this.invoice.status] || false;
      }

      return true;
    },
    dialog_title() {
      const edit_text = this.is_status_editable
        ? "Editar Factura"
        : "Ver Factura";

      return this.is_editing ? edit_text : "Crear Factura";
    },
    available_lines() {
      return this.uninvoiced_lines.filter((quotation_line) => {
        let line = this.data.lines.find((invoice_line) => {
          return quotation_line.id == invoice_line.quotation_line_id;
        });
        return !Boolean(line);
      });
    },
    unrevoked_options() {
      if (!this.unrevoked_invoices) {
        return [];
      }

      return this.unrevoked_invoices.map((i) => {
        return {
          label: i.code,
          value: i.id,
        };
      });
    },
    is_dental() {
      if (!this.clinic) return false;
      return this.clinic.type == "dental";
    },
    is_error() {
      return this.invoice && this.invoice.status == 'error';
    },
    verifactu_status() {
      if (!this.invoice) {
        return null;
      }

      return this.invoice.verifactu_status;
    },
    has_notice() {
      if (!this.invoice || this.invoice.status !== 'published') {
        return false;
      }

      const vfactu_status = this.verifactu_status;
      if (!vfactu_status || (vfactu_status.error || false)) {
        return false;
      }

      const registration = vfactu_status.content.transmission.registration || null;
      return (registration == 'REQUIRES_INSPECTION') || (registration == 'REQUIRES_CORRECTION');
    },
    notice_tooltip() {
      if (!this.has_notice) {
        return '';
      }

      let validations = this.verifactu_status.content.validations.map((validation) => {
        return `${validation.code} - ${validation.description}`;
      });

      return validations.join(' | ');
    },
    validation_name() {
      if (this.is_other_customer) {
        return this.customer_name;
      }
      return this.customer_real_name;
    },
    validation_nif() {
      if (this.is_other_customer) {
        return this.customer_nif;
      }

      return this.customer_real_nif;
    },
    is_nif_empty() {
      const customer_nif = this.validation_nif;
      return !(Boolean(customer_nif) && customer_nif != '');
    },
    can_delete() {
      return (this.is_editing && this.drafts_enabled && this.invoice.status == 'draft');
    },
    ...mapGetters(["msgwindow", "errorwindow", "user_capabilities"]),
  },
  methods: {
    getLinePrice(line) {
      return WabiDent.fmtMoney(Number(line.price) * Number(line.quantity));
    },
    getLineNetPrice(line) {
      return WabiDent.fmtMoney(
        Number(line.price) *
        Number(line.quantity) *
        (1 - Number(line.discount) / 100.0) -
        line.abs_discount
      );
    },
    getLineDesc(line) {
      if (line.product) {
        return line.product.name + this.prettyTeeth(line);
      } else {
        return "(PRODUCTO BORRADO) " + this.prettyTeeth(line);
      }
    },
    showNew() {
      this.payment_id = null;
      this.is_editing = false;
      this.invoice = null;
      this.data.lines = [];
      this.data.code = "";
      this.data.date = new Date().toISODate();
      this.data.notes = "";
      this.uninvoiced_lines = [];
      this.selected_line = null;
      this.is_revocation = false;
      this.revocation_type = 'difference';
      this.revocation_invoice_id = null;
      this.is_other_customer = false;
      this.customer_phone = "";
      this.customer_email = "";
      this.customer_address = "";
      this.customer_city = "";
      this.customer_zipcode = "";
      this.customer_province = "";

      this.getUninvoicedLines();
      this.getUnrevokedInvoices();
      this.$refs.modal.show();
    },
    showNewPayment(payment) {
      this.payment_id = payment.id;
      this.is_editing = false;
      this.invoice = null;
      this.data.lines = [];
      this.data.code = "";
      this.data.date = new Date().toISODate();
      this.data.notes = "";
      this.uninvoiced_lines = [];
      this.selected_line = null;
      this.is_revocation = false;
      this.revocation_type = 'difference';
      this.revocation_invoice_id = null;
      this.is_other_customer = false;
      this.customer_phone = "";
      this.customer_email = "";
      this.customer_address = "";
      this.customer_city = "";
      this.customer_zipcode = "";
      this.customer_province = "";
      this.fillFromPayment(payment);
      this.$refs.modal.show();
    },
    showEdit(invoice) {
      this.payment_id = null;
      this.is_editing = true;
      this.fillFrom(invoice);
      this.uninvoiced_lines = [];
      this.selected_line = null;
      this.getUninvoicedLines();
      this.getUnrevokedInvoices();
      this.$refs.modal.show();
    },
    submit() {
      const title = this.is_editing
        ? "¿Desea Modificar la Factura?"
        : "¿Desea Crear la Factura?";

      const action = this.is_editing ? "Modificar" : "Crear";

      this.errorwindow.show("Confirmación", title, action, "Cancelar", () => {
        if (this.is_editing) {
          this.updateInvoice();
        } else {
          this.addInvoice();
        }
      });
    },
    publish() {
      if (!this.verifactu_enabled) {
        this.doPublish();
      }

      if (!this.is_loading) {
        this.is_loading = true;
      }

      this.msgwindow.info("Validando NIF Factura ...");

      axios.post('/verifactu/validate_aeat', {
        nif: this.validation_nif,
        name: this.validation_name,
      }).then((response) => {
        console.log(response.data);
        this.is_loading = false;
        if (response.data.error) {
          this.msgwindow.success("Error verificando NIF Factura con Verifactu - Por favor contacte con el Administrador");
        } else {
          const validation_status = response.data.results[0].content.result || null;
          if (validation_status != 'VALID') {
            this.doPublish('<br><br><strong>Advertencia:</strong> La AEAT no ha podido verificar que el NIF y Nombre aportados sean válidos. Es posible que tenga que rectificar o anular la factura en un futuro.');
          } else {
            this.doPublish();
          }
        }
      }).catch((error) => {
        console.log('[validateAEAT]', error);
        this.msgwindow.success("Error verificando NIF Factura con Verifactu - Por favor contacte con el Administrador");
      }).then(() => {
        this.is_loading = false;
      });

    },
    doPublish(extra_msg = '') {
      this.errorwindow.show(
        "Confirmación",
        "¿Publicar la Factura? Una vez publicada, no se podra modificar." + extra_msg,
        "Publicar",
        "Cancelar",
        () => {
          this.invoice.status = "pending_publish";
          this.updateInvoice();
        }
      );
    },
    onRevoke() {
      this.errorwindow.show(
        "Confirmación",
        "¿Desea generar una Factura Rectificativa?",
        "Aceptar",
        "Cancelar",
        () => {
          if (!this.is_loading) {
            this.is_loading = true;
            axios
              .post(`/invoice/${this.invoice.id}/revoke`)
              .then((response) => {
                this.$emit("submit");
                if (response.data.error) {
                  this.msgwindow.error("Error: " + response.data.error);
                } else {
                  this.msgwindow.success("Factura Rectificada");
                }
                this.$refs.modal.hide();
              })
              .catch((err) => {
                console.log("[ERR] revokeInvoice", err);
                this.msgwindow.error("Error rectificando factura!");
                this.$refs.modal.hide();
              })
              .then(() => {
                this.is_loading = false;
              });
          }
        }
      );
    },
    onAnull() {
      this.errorwindow.show(
        "Confirmación",
        "¿Desea <strong>ANULAR</strong> esta factura?." +
        "<br><br>Dejará de tener validez a efectos contables, pero el número no se podrá reutilizar" +
        "<br><br><strong>IMPORTANTE:</strong> La anulación de facturas sólo debe usarse para descartar " +
        "facturas emitidas por servicios que no se prestaron, emitidas por error, o para la realización de pruebas." +
        " Para corregir facturas debe siempre hacerse una RECTIFICACIÓN",
        "Aceptar",
        "Cancelar",
        () => {
          if (!this.is_loading) {
            this.is_loading = true;
            axios
              .post(`/invoice/${this.invoice.id}/anull`)
              .then((response) => {
                this.$emit("submit");
                if (response.data.error) {
                  this.msgwindow.error("Error: " + response.data.error);
                } else {
                  this.msgwindow.success("Factura Anulada");
                }
                this.$refs.modal.hide();
              })
              .catch((err) => {
                console.log("[ERR] anullInvoice", err);
                this.msgwindow.error("Error anulando factura!");
                this.$refs.modal.hide();
              })
              .then(() => {
                this.is_loading = false;
              });
          }
        }
      );
    },
    onRemedy() {
      this.errorwindow.show(
        "Confirmación",
        "¿Desea <strong>SUBSANAR</strong> esta factura?." +
        "<br><br>Las subsanaciones son sólo correcciones formales para realizar con la AEAT, pero no con el cliente" +
        "<br><br><strong>IMPORTANTE:</strong> La subsanación de facturas es sólo es para" +
        " corregir validaciones técnicas con la Administración. La factura original sigue siendo válida." +
        " Para corregir los datos del cliente debe siempre hacerse una RECTIFICACIÓN",
        "Aceptar",
        "Cancelar",
        () => {
          if (!this.is_loading) {
            this.is_loading = true;
            axios
              .post(`/invoice/${this.invoice.id}/remedy`)
              .then((response) => {
                this.$emit("submit");
                if (response.data.error) {
                  this.msgwindow.error("Error: " + response.data.error);
                } else {
                  this.msgwindow.success("Factura Subsanada");
                }
                this.$refs.modal.hide();
              })
              .catch((err) => {
                console.log("[ERR] anullInvoice", err);
                this.msgwindow.error("Error subsanando factura!");
                this.$refs.modal.hide();
              })
              .then(() => {
                this.is_loading = false;
              });
          }
        }
      );
    },
    onDelete() {
      this.errorwindow.show('Confirmación', '¿Desea borrar la Factura?. Este proceso es irreversible.', 'Borrar', 'Cancelar', () => {
        this.doDelete()
      });
    },
    doDelete() {
      if (!this.is_loading) {
        this.is_loading = true;
        axios
          .post(`/invoice/${this.invoice.id}/delete`)
          .then((response) => {
            this.$emit("submit");
            if (response.data.error) {
              this.msgwindow.error("Error: " + response.data.error);
            } else {
              this.msgwindow.success("Factura Borrada");
            }
            this.$refs.modal.hide();
          })
          .catch((err) => {
            this.msgwindow.error("Error borrando Factura - Contacte con el Administrador !");
            this.$refs.modal.hide();
          })
          .then(() => {
            this.is_loading = false;
          });
      }
    },
    updateInvoice() {
      if (!this.is_loading) {
        this.is_loading = true;
        let params = {
          customer_id: this.customer.id,
          lines: this.data.lines,
          code: this.data.code,
          notes: this.data.notes,
          created_at: this.data.date,
          clinic_id: this.clinic ? this.clinic.id : null,
          is_other_customer: this.is_other_customer,
          customer_name: this.customer_name,
          customer_nif: this.customer_nif,
          customer_phone: this.customer_phone,
          customer_email: this.customer_email,
          customer_address: this.customer_address,
          customer_city: this.customer_city,
          customer_zipcode: this.customer_zipcode,
          customer_province: this.customer_province,
          is_revocation: this.is_revocation,
          revocation_invoice_id: this.revocation_invoice_id,
          revocation_type: this.revocation_type,
          payment_id: this.payment_id,
          status: this.invoice.status,
        };
        axios
          .post(`/invoice/${this.invoice.id}/update`, params)
          .then((response) => {
            this.$emit("submit");
            if (response.data.error) {
              this.msgwindow.error("Error: " + response.data.error);
            } else {
              this.msgwindow.success("Factura modificada");
            }
            this.$refs.modal.hide();
          })
          .catch((err) => {
            this.msgwindow.error("Error modificando factura");
            console.log("[ERR] updateInvoice", err);
          })
          .then(() => {
            this.is_loading = false;
          });
      }
    },
    addInvoice() {
      if (!this.is_loading) {
        this.is_loading = true;
        let params = {
          customer_id: this.customer.id,
          lines: this.data.lines,
          code: this.data.code,
          notes: this.data.notes,
          created_at: this.data.date,
          clinic_id: this.clinic ? this.clinic.id : null,
          is_other_customer: this.is_other_customer,
          customer_name: this.customer_name,
          customer_nif: this.customer_nif,
          customer_phone: this.customer_phone,
          customer_email: this.customer_email,
          customer_address: this.customer_address,
          customer_city: this.customer_city,
          customer_zipcode: this.customer_zipcode,
          customer_province: this.customer_province,
          is_revocation: this.is_revocation,
          revocation_invoice_id: this.revocation_invoice_id,
          revocation_type: this.revocation_type,
          payment_id: this.payment_id,
        };
        axios
          .post(`/invoice`, params)
          .then((response) => {
            this.$emit("submit");
            this.msgwindow.success("Factura creada");
            this.$refs.modal.hide();
          })
          .catch((err) => {
            this.msgwindow.error("Error creando factura");
            console.log("[ERR] addInvoice", err);
          })
          .then(() => {
            this.is_loading = false;
          });
      }
    },
    fillFromPayment(payment) {
      this.data.lines.push({
        desc: payment.notes,
        price: payment.amount,
        discount: 0,
        abs_discount: 0,
        quantity: 1,
        vat: payment.vat,
        quotation_line_id: null,
        treatment_id: null,
      });
    },
    fillFrom(invoice) {
      this.invoice = invoice;
      this.data.code = invoice.code;
      this.data.lines = WabiDent.cloneObj(invoice.invoicelines);
      this.data.date = moment(invoice.created_at).format("YYYY-MM-DD");
      this.data.notes = invoice.notes;
      this.is_other_customer = invoice.is_other_customer;
      this.customer_name = invoice.customer_name;
      this.customer_nif = invoice.customer_nif;
      this.customer_phone = invoice.customer_phone;
      this.customer_email = invoice.customer_email;
      this.customer_address = invoice.customer_address;
      this.customer_city = invoice.customer_city;
      this.customer_zipcode = invoice.customer_zipcode;
      this.customer_province = invoice.customer_province;
      this.is_revocation = invoice.is_revocation;
      this.revocation_type = invoice.revocation_type;
      this.revocation_invoice_id = invoice.revocation_invoice_id;
    },
    removeLine(line, index) {
      if (!this.is_status_editable) {
        return;
      }

      this.data.lines = this.data.lines.filter((v, i) => {
        return i != index;
      });
    },
    addLine() {
      this.data.lines.push({
        desc: "",
        price: 0,
        discount: 0,
        abs_discount: 0,
        quantity: 1,
        vat: 0,
        quotation_line_id: null,
        treatment_id: null,
      });
    },
    addUninvoicedLine(line) {
      this.data.lines.push({
        desc: this.getLineDesc(line),
        price: line.price,
        vat: line.product.vat,
        discount: line.discount,
        abs_discount: line.abs_discount,
        quantity: line.quantity,
        quotation_line_id: line.id,
        treatment_id: null,
      });
    },
    prettyTeeth(line) {
      if (!line.teeth || !line.teeth.length) return "";

      return (
        "(" +
        line.teeth.reduce((r, x) => {
          return r + " " + x;
        }, "") +
        ")"
      );
    },
    getUnrevokedInvoices() {
      if (!this.customer) return;

      axios
        .get(`/customer/${this.customer.id}/unrevoked_invoices`)
        .then((response) => {
          this.unrevoked_invoices = response.data;
        })
        .catch((err) => {
          this.msgwindow.error("Error obteniendo tratamientos sin facturar");
        });
    },
    searchUnrevoked(search, loading) {
      let params = {
        search: search,
      };

      axios
        .get(`/customer/${this.customer.id}/unrevoked_invoices`, { params })
        .then((response) => {
          this.unrevoked_invoices = response.data;
        })
        .catch((err) => {
          this.msgwindow.error("Error obteniendo tratamientos sin facturar");
        });
    },
    getUninvoicedLines() {
      if (!this.customer) return;

      axios
        .get(`/customer/${this.customer.id}/uninvoiced_lines`)
        .then((response) => {
          this.uninvoiced_lines = response.data;
        })
        .catch((err) => {
          console.log("[ERR] getUninvoicedLines ", err);
          this.msgwindow.error("Error obteniendo tratamientos sin facturar");
        });
    },
  },
  mounted() { },
};
</script>

<style lang="scss">
#customer-show-invoice {
  .modal-dialog {
    max-width: 75%;
  }

  .buttons-column {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2rem;
  }

  .customer-info {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background-color: var(--theme-grey-bg);
    border-bottom: 1px var(--theme-line-color) solid;
    padding: 15px;

    .customer-photo {
      width: 62px;
      height: 62px;
      background-size: cover;
      background-position: center center;
      border-radius: 31px;
      margin-left: 4px;
    }

    .customer-data {
      padding: 11px 18px;
      font-size: 20px;
      line-height: 1.25;
      color: var(--theme-text-color);
    }
  }

  .lines-table {
    thead {
      th {
        border-top: none;
        border-bottom: 1px var(--theme-grey) solid;
        color: var(--theme-grey);
        font-size: 11px;
        letter-spacing: 0.16px;
        font-weight: 300;
        line-height: 1.82;
      }
    }

    tbody {
      td {
        font-size: 16px;
        color: var(--theme-text-color);
        border: none;

        .total {
          display: block;
          font-size: 11px;
          line-height: 1.82;
          font-weight: 300;
          color: var(--theme-grey);
          letter-spacing: 0.16px;
          margin-top: 9px;
          border-top: 1px var(--theme-grey) solid;
          padding: 14px 17px 0 0;
        }

        &.input {
          padding: 7px;
        }
      }

      .trash-icon {
        cursor: pointer;
        color: var(--theme-grey);
        transition: all 0.2s;
        vertical-align: middle;
        text-align: center;

        &:hover {
          color: var(--theme-color-absent);
        }

        svg {
          fill: currentColor;
        }
      }
    }
  }

  .add-line {
    font-size: 13px;
    font-weight: bold;
    color: var(--theme-line-color);
    transition: all 0.2s;
    cursor: pointer;

    i {
      font-size: 13px;
    }

    &:hover {
      color: var(--theme-color);
    }
  }
}
</style>
